/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useTranslation from 'next-translate/useTranslation';
import ReCAPTCHA from 'react-google-recaptcha';
import classNames from 'classnames';

import Axios from '@/utils/axios';
import { CLIENT_URL, envVariables } from '@/constants/index';
import { Arrow, UsersCircule } from '@/icons/index';
import {
  AttachFiles,
  Button,
  CheckboxWithText,
  InformationBlock,
  Input,
  ScreenLoader,
  SectionTitle,
  Select,
  InformationModal,
  ErrorModal,
} from '@/ui/index';

import VacanciesListItem from './VacanciesListItem';

import styles from './Vacancies.module.scss';

const Modal = dynamic(() => import('@/ui/Modal/Modal'));
const Vacancies = ({ data }) => {
  const positionsLength = 10;
  const { t, lang } = useTranslation();
  const FormSchema = Yup.object().shape({
    full_name: Yup.string()
      .required(t('common:Required'))
      .matches(/^[а-яА-ЯёЁa-zA-ZəçğıöşüƏÇĞİÖŞÜ\s]+$/, t('common:OnlyLetters')),
    phone: Yup.string()
      .required(t('common:Required'))
      .matches(
        /^994(10|50|51|55|70|77|99)\d{7}$/,
        t('common:Invalid mobile number'),
      ),
    email: Yup.string()
      .email(t('common:Invalid email'))
      .required(t('common:Invalid email')),
    captcha: Yup.string().required(t('common:required')),
    file: Yup.mixed().test(t('common:required'), (file) => {
      if (file.length) return true;
      return false;
    }),
  });

  const spreadDataForCity = [
    {
      id: 0,
      name: t('common:All'),
    },
    ...data?.cities,
  ];
  const spreadDataForDepartment = [
    {
      id: 0,
      name: t('common:All'),
    },
    ...data?.departments,
  ];

  const [activeModal, setActiveModal] = useState(false);
  const [positionId, setPositionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [sortValueCity, setSortValueCity] = React.useState({});
  const [sortValueCategory, setSortValueCategory] = React.useState({});
  const [filteredData, setFilteredData] = React.useState([]);
  const [captcha, setCaptcha] = React.useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [agreement, setAgreement] = useState(false);

  const filteredDataLength = filteredData.length > 0;
  const content = {
    title: t('common:noJobsAvailable'),
    text: t('common:informationBlockDescription'),
    buttonText: t('common:Send'),
  };

  useEffect(() => {
    setSortValueCity({
      id: 0,
      name: t('common:All'),
    });
    setSortValueCategory({
      id: 0,
      name: t('common:All'),
    });
  }, [lang]);

  const formik = useFormik({
    initialValues: {
      full_name: '',
      phone: '',
      email: '',
      file: [],
      position_id: '',
    },
    validationSchema: FormSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append('full_name', values?.full_name);
      formData.append('phone', values?.phone);
      formData.append('email', values?.email);
      formData.append('file', values?.file[0]);
      formData.append('position_id', positionId);
      formData.append('g-recaptcha-response', captcha);

      try {
        setLoading(true);
        const response = await Axios.post('job-applications/', formData, {
          baseURL: CLIENT_URL,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response) {
          setShowSuccess(true);
        }
      } catch (error) {
        setShowError(true);
      } finally {
        setLoading(false);
        setActiveModal(false);
        setAgreement(false);
        formik.resetForm();
      }
    },
  });
  const modalHandler = () => {
    setShowSuccess(false);
    setShowError(false);
  };
  React.useEffect(() => {
    if (sortValueCity?.id === 0 && sortValueCategory?.id === 0) {
      setFilteredData(data?.positions);
    } else if (sortValueCity?.id === 0 && sortValueCategory?.id !== 0) {
      const filterByCategory = data?.positions?.filter(
        (item) =>
          String(item?.department?.id) === String(sortValueCategory?.id),
      );
      setFilteredData(filterByCategory);
    } else if (sortValueCity?.id !== 0 && sortValueCategory?.id === 0) {
      const filterByCity = data?.positions?.filter(
        (item) => String(item?.city_id) === String(sortValueCity?.id),
      );
      setFilteredData(filterByCity);
    } else {
      const filtered = data?.positions.filter((item) => {
        return (
          String(item?.city_id) === String(sortValueCity?.id) &&
          String(item?.department?.id) === String(sortValueCategory?.id)
        );
      });
      setFilteredData(filtered);
    }
  }, [sortValueCity?.id, sortValueCategory?.id, data]);

  const showElement = () => {
    setIsShow(true);
  };

  const onApply = () => {
    setActiveModal(true);
  };
  const onChange = (value) => {
    formik.setFieldValue('captcha', value);
    setCaptcha(value);
  };
  const onCloseModal = (value) => {
    formik.resetForm();
    setActiveModal(value);
  };
  const renderVacancy = (item, index) => {
    return (
      <VacanciesListItem
        onApply={onApply}
        item={item}
        key={index}
        cities={data?.cities}
        setPositionId={setPositionId}
      />
    );
  };
  return (
    <div className='section'>
      <div className='wrapper small-margin'>
        <SectionTitle className={styles.title}>{data?.title}</SectionTitle>
        <div className={styles.content_wrapper}>
          <div className={styles.titleWrapper}>
            <ScreenLoader visible={loading} />
            <div className={styles.sorts}>
              {data?.cities && (
                <Select
                  value={sortValueCity}
                  data={spreadDataForCity}
                  label={t('common:sortByCity')}
                  onChange={setSortValueCity}
                />
              )}
              {data?.departments && (
                <Select
                  value={sortValueCategory}
                  data={spreadDataForDepartment}
                  label={t('common:sortByCategory')}
                  onChange={setSortValueCategory}
                />
              )}
            </div>
          </div>
          <section className={styles.vacancies}>
            {data?.positions && (
              <div className={styles.vacancies__wrapper}>
                {filteredDataLength ? (
                  <div
                    className={classNames(styles.vacancies__list, {
                      [styles.show]: isShow,
                    })}
                  >
                    {filteredData?.map(renderVacancy)}
                  </div>
                ) : (
                  <InformationBlock
                    content={content}
                    icon={<UsersCircule />}
                    onClick={onApply}
                    secondary
                    noBackgroundImage
                  />
                )}
                {data?.positions > positionsLength && (
                  <div
                    className={classNames(styles['vacancies-opener'], {
                      [styles.hide]: !!isShow,
                    })}
                    onClick={showElement}
                  >
                    <span className={styles['vacancies-opener__text']}>
                      {t('common:See more')}
                    </span>
                    <Arrow width={24} height={24} />
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
      {activeModal && (
        <Modal active={activeModal} setActive={onCloseModal}>
          <div className={'title'}>{t('common:Modal title')}</div>
          <div className={'note'}>{t('common:Modal description')}</div>
          <form
            className={`${styles.modalContent} grid-extend equal`}
            onSubmit={formik.handleSubmit}
          >
            <div className={`${styles.field} ${styles.half}`}>
              <Input
                onBlur={formik.handleBlur('full_name')}
                error={formik.touched.full_name && formik.errors.full_name}
                value={formik.values.full_name}
                onChange={(value) => formik.setFieldValue('full_name', value)}
                label={t('common:Full name')}
                placeholder={t('common:fullname')}
              />
            </div>

            <div className={`${styles.field} ${styles.half}`}>
              <Input
                onBlur={formik.handleBlur('phone')}
                error={formik.touched.phone && formik.errors.phone}
                value={formik.values.phone}
                onChange={(value) => formik.setFieldValue('phone', value)}
                label={t('common:Contact')}
                placeholder={'+994-XX-XXX-XX-XX'}
                mask={'+\\9\\9\\4-99-999-99-99'}
              />
            </div>

            <div className={styles.field}>
              <Input
                onBlur={formik.handleBlur('email')}
                error={formik.touched.email && formik.errors.email}
                type='email'
                value={formik.values.email}
                onChange={(value) => formik.setFieldValue('email', value)}
                label={t('common:Email')}
                placeholder={t('common:Subscribe placeholder')}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fileTitle}>
                {t('common:Attach file')}
              </span>
              <AttachFiles
                setFiles={(file) => formik.setFieldValue('file', file)}
                htmlText={data?.file_browse_text}
              />
            </div>

            <div className={styles.field}>
              <CheckboxWithText
                className={styles.checkbox}
                name='agreement'
                checked={agreement}
                onChange={(e) => setAgreement(e.target.checked)}
                isHtml
              >
                {data?.agreement_text}
              </CheckboxWithText>
            </div>
            <ReCAPTCHA
              sitekey={envVariables.NEXT_PUBLIC_RECAPTCHA_API_KEY}
              onChange={onChange}
              hl={lang}
            />
            <div className={styles.field}>
              <Button
                className={styles.button}
                type={'submit'}
                disabled={!agreement || !formik.isValid}
              >
                {t('common:Apply')}
              </Button>
            </div>
          </form>
        </Modal>
      )}
      <InformationModal
        type={'white'}
        title={t('common:vacancies.title')}
        description={t('common:vacancies.desc')}
        active={showSuccess}
        onClose={modalHandler}
      />
      <ErrorModal active={showError} setActive={modalHandler}>
        {t('common:vacancies.error')}
      </ErrorModal>
    </div>
  );
};

export default Vacancies;
